/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Serialize CL-F03 classroom access-grant requests through the authenticated API client.
 * @state-owner request-local transport
 * @allowed-imports API client public type; same-module access-grant model types
 * @forbidden React; query cache; routes
 * @public-api classroom access-grant query and mutation hooks
 * @side-effects performs authenticated access-grant reads and commands
 * @naming-hook GET -> use<Resource>; POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>; DELETE -> useDelete<Resource>
 * @naming-path plural lowercase nouns; nested ownership; filters in the query string
 * @naming-body camelCase; idempotencyKey and expectedRevision are explicit command inputs
 * @tests modules/classrooms/api/access-grants.test.ts
 * @security-boundary authenticated workspace to FastAPI classroom access-grant endpoints
 * @data-classification invitation addresses, projected grant metadata, and reveal-once tokens
 * @trust-model request bodies and responses are untrusted; the backend re-authorizes scope and plaintext tokens remain component-local
 */

import type { VispoApiClient } from '@vispo/api-client';
import type { ClassroomAccessGrant, StudentJoinLinkSummary } from '../model/access-grants';

export interface ClassroomAccessGrantApiResponse {
  id: string;
  organization_id: string;
  classroom_id: string;
  kind: 'invitation' | 'student_join_link';
  grant_role: 'teacher' | 'student';
  recipient_email: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  max_uses: number | null;
  use_count: number;
  expires_at: string;
  last_sent_at: string | null;
  revision: number;
  token?: string | null;
}

export function mapAccessGrant(value: ClassroomAccessGrantApiResponse): ClassroomAccessGrant {
  return {
    id: value.id,
    organizationId: value.organization_id,
    classroomId: value.classroom_id,
    kind: value.kind,
    grantRole: value.grant_role,
    recipientEmail: value.recipient_email,
    status: value.status,
    maxUses: value.max_uses,
    useCount: value.use_count,
    expiresAt: value.expires_at,
    lastSentAt: value.last_sent_at,
    revision: value.revision,
    token: value.token ?? null,
  };
}

export function classroomInvitationsRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  organizationId: string,
  status: string,
) {
  return apiClient.request<ClassroomAccessGrantApiResponse[]>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/invitations?${new URLSearchParams({ organizationId, status })}`,
  );
}

export function createClassroomInvitationRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  body: {
    organizationId: string;
    recipientEmail: string;
    grantRole: 'teacher' | 'student';
    expiresAt: string;
    idempotencyKey: string;
  },
) {
  return apiClient.request<ClassroomAccessGrantApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/invitations`,
    { method: 'POST', body },
  );
}

export function createStudentJoinLinkRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  body: {
    organizationId: string;
    expiresAt: string;
    maxUses?: number | null;
    idempotencyKey: string;
  },
) {
  return apiClient.request<ClassroomAccessGrantApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/student-join-links`,
    { method: 'POST', body },
  );
}

export function resendClassroomInvitationRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  invitationId: string,
  body: { organizationId: string; expectedRevision: number; idempotencyKey: string },
) {
  return apiClient.request<ClassroomAccessGrantApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
    { method: 'POST', body },
  );
}

export function revokeClassroomInvitationRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  invitationId: string,
  body: { organizationId: string; expectedRevision: number; idempotencyKey: string },
) {
  return apiClient.request<ClassroomAccessGrantApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/invitations/${encodeURIComponent(invitationId)}`,
    { method: 'DELETE', body },
  );
}

export interface StudentJoinLinkSummaryApiResponse {
  id: string;
  organization_id: string;
  classroom_id: string;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  max_uses: number | null;
  use_count: number;
  expires_at: string;
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
  revision: number;
}

export function mapStudentJoinLinkSummary(
  value: StudentJoinLinkSummaryApiResponse,
): StudentJoinLinkSummary {
  return {
    id: value.id,
    organizationId: value.organization_id,
    classroomId: value.classroom_id,
    status: value.status,
    maxUses: value.max_uses,
    useCount: value.use_count,
    expiresAt: value.expires_at,
    createdAt: value.created_at,
    updatedAt: value.updated_at,
    revokedAt: value.revoked_at,
    revision: value.revision,
  };
}

export function studentJoinLinksRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  organizationId: string,
) {
  return apiClient.request<StudentJoinLinkSummaryApiResponse[]>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/student-join-links?${new URLSearchParams({ organizationId })}`,
  );
}

export function updateStudentJoinLinkRequest(
  apiClient: VispoApiClient,
  classroomId: string,
  joinLinkId: string,
  body: {
    organizationId: string;
    expectedRevision: number;
    expiresAt?: string;
    maxUses?: number | null;
    state?: 'revoked';
    idempotencyKey: string;
  },
) {
  return apiClient.request<ClassroomAccessGrantApiResponse>(
    `/api/v1/classrooms/${encodeURIComponent(classroomId)}/student-join-links/${encodeURIComponent(joinLinkId)}`,
    { method: 'PATCH', body },
  );
}
